import {
  Colors,
  GrayScaleColors,
  IconPack,
  Multimedia,
  Palette,
  PaletteType,
  TypographyClass,
  TypographyConfiguration,
} from "../../../gen/witbooking/backoffice/common/common_pb";
import { PropertyConfigurationAPIPromiseClient } from "../../../gen/witbooking/backoffice/propertyconfiguration/v1/propertyconfiguration_api_grpc_web_pb";
import {
  EngineAppearance,
  EngineBookingAdvantagePositionType,
  EngineBookingAdvantageVariant,
  GetPropertyConfigurationRequest,
  UpdateEngineAppearanceRequest,
} from "../../../gen/witbooking/backoffice/propertyconfiguration/v1/propertyconfiguration_api_pb";
import { addAuthzMetadataGrpcCall, buildGrpcClient } from "../../../lib/grpc";
import { Image } from "../../shared/widgets/ImageManager";
import {
  EngineAppearanceEntity,
  SimpleEngineAppearanceEntity,
} from "../domain/engineAppearance.entity";

const propertyconfigurationClient: PropertyConfigurationAPIPromiseClient = buildGrpcClient(
  PropertyConfigurationAPIPromiseClient
);

function oppositeColorHex(rgbaColor: string): string {
  const rgbaRegex = /rgba\((\d+),\s*(\d+),\s*(\d+),\s*\d+\)/;
  const match = rgbaColor.match(rgbaRegex);
  if (!match) {
    throw new Error("Invalid RGBA format");
  }

  const [, r, g, b] = match;
  const red = parseInt(r, 10);
  const green = parseInt(g, 10);
  const blue = parseInt(b, 10);

  return red + green + blue > 255 * 1.5 ? "rgba(0, 0, 0, 1)" : "rgba(255, 255, 255, 1)";
}

function colorsFromObject(colors: Colors.AsObject | undefined): Colors {
  if (!colors) {
    throw new Error("Function not implemented.");
  }
  const result = new Colors();
  result.setLight(colors.light);
  result.setMain(colors.main);
  result.setDark(colors.dark);
  result.setContrasttext(colors.contrasttext);
  return result;
}

function grayScaleColorsFromObject(colors: GrayScaleColors.AsObject | undefined): GrayScaleColors {
  if (!colors) {
    throw new Error("Function not implemented.");
  }
  const result = new GrayScaleColors();
  result.setGray25(colors.gray25);
  result.setGray50(colors.gray50);
  result.setGray100(colors.gray100);
  result.setGray200(colors.gray200);
  result.setGray300(colors.gray300);
  return result;
}

function paletteFromObject(palette: Palette.AsObject | undefined): Palette {
  if (!palette) {
    throw new Error("Function not implemented.");
  }
  const result = new Palette();
  result.setId(palette.id);
  result.setTicker(palette.ticker);
  result.setType(palette.type);
  result.setPrimary(colorsFromObject(palette.primary));
  result.setSecondary(colorsFromObject(palette.secondary));
  result.setError(colorsFromObject(palette.error));
  result.setSuccess(colorsFromObject(palette.success));
  result.setBlack(colorsFromObject(palette.black));
  result.setWhite(colorsFromObject(palette.white));
  result.setGray(grayScaleColorsFromObject(palette.gray));
  return result;
}
function typographyClassFromObject(obj: TypographyClass.AsObject | undefined): TypographyClass {
  if (!obj) {
    throw new Error("Function not implemented.");
  }
  const result = new TypographyClass();
  result.setFontsize(obj.fontsize);
  result.setFontweight(obj.fontweight);
  result.setFontfamily(obj.fontfamily);
  result.setLineheight(obj.lineheight);
  result.setFontstyle(obj.fontstyle);
  result.setLetterspacing(obj.letterspacing);
  return result;
}
function typographyConfigurationFromObject(
  obj: TypographyConfiguration.AsObject | undefined
): TypographyConfiguration {
  if (!obj) {
    throw new Error("Function not implemented.");
  }
  const result = new TypographyConfiguration();
  result.setFontsize(obj.fontsize);
  result.setFontweightlight(obj.fontweightlight);
  result.setFontweightregular(obj.fontweightregular);
  result.setFontweightmedium(obj.fontweightmedium);
  result.setFontweightbold(obj.fontweightbold);
  result.setHtmlfontsize(obj.htmlfontsize);
  result.setFontfamily(obj.fontfamily);
  result.setH1(typographyClassFromObject(obj.h1));
  result.setH2(typographyClassFromObject(obj.h2));
  result.setH3(typographyClassFromObject(obj.h3));
  result.setH4(typographyClassFromObject(obj.h4));
  result.setH5(typographyClassFromObject(obj.h5));
  result.setH6(typographyClassFromObject(obj.h6));
  result.setSubtitle1(typographyClassFromObject(obj.subtitle1));
  result.setSubtitle2(typographyClassFromObject(obj.subtitle2));
  result.setSubtitle3(typographyClassFromObject(obj.subtitle3));
  result.setBody1(typographyClassFromObject(obj.body1));
  result.setBody2(typographyClassFromObject(obj.body2));
  result.setBody3(typographyClassFromObject(obj.body3));
  result.setButton(typographyClassFromObject(obj.button));
  result.setCaption(typographyClassFromObject(obj.caption));
  result.setOverline(typographyClassFromObject(obj.overline));
  return result;
}
function iconPackFromObject(obj: IconPack.AsObject | undefined): IconPack {
  if (!obj) {
    throw new Error("Function not implemented.");
  }
  const result = new IconPack();
  result.setTicker(obj.ticker);
  result.setName(obj.name);
  return result;
}
function multimediaFromObject(obj: Multimedia.AsObject | undefined): Multimedia {
  if (!obj) {
    throw new Error("Function not implemented.");
  }
  const result = new Multimedia();
  result.setId(obj.id);
  result.setFilename(obj.filename);
  result.setUrl(obj.url);
  return result;
}
function bookingAdvantageVariantToProto(val: unknown): EngineBookingAdvantageVariant {
  if (typeof val === "number") return val as EngineBookingAdvantageVariant;
  if (typeof val === "string") {
    switch (val.toLowerCase()) {
      case "independent":
        return EngineBookingAdvantageVariant.INDEPENDENT;
      case "combined":
        return EngineBookingAdvantageVariant.COMBINED;
    }
  }
  return EngineBookingAdvantageVariant.ENGINE_BOOKING_ADVANTAGE_VARIANT_EMPTY;
}

function bookingAdvantagePositionToProto(val: unknown): EngineBookingAdvantagePositionType {
  if (typeof val === "number") return val as EngineBookingAdvantagePositionType;
  if (typeof val === "string") {
    switch (val.toLowerCase()) {
      case "top":
        return EngineBookingAdvantagePositionType.POSITION_TOP;
      case "bottom":
        return EngineBookingAdvantagePositionType.POSITION_BOTTOM;
    }
  }
  return EngineBookingAdvantagePositionType.ENGINE_BOOKING_ADVANTAGE_POSITION_TYPE_EMPTY;
}

function engineAppearanceFromObject(obj: EngineAppearanceEntity): EngineAppearance {
  const result = new EngineAppearance();
  result.setDarkmode(obj.darkmode);
  result.setPalette(paletteFromObject(obj.palette));
  result.setTypographyconfiguration(typographyConfigurationFromObject(obj.typographyconfiguration));
  result.setMobiletypographyconfiguration(
    typographyConfigurationFromObject(obj.mobiletypographyconfiguration)
  );
  result.setIconpack(iconPackFromObject(obj.iconpack));
  result.setAccommodationresultsformattype(obj.accommodationresultsformattype);
  result.setInventoryresultsformattype(obj.inventoryresultsformattype);
  result.setDisplaymode(obj.displaymode);
  result.setBookingadvantagesposition(obj.bookingadvantagesposition);
  result.setBookingadvantagesloyaltymodule(obj.bookingadvantagesloyaltymodule);
  result.setButtonroundtype(obj.buttonroundtype);
  result.setTagroundtype(obj.tagroundtype);
  result.setBookingformroundtype(obj.bookingformroundtype);
  result.setCardroundtype(obj.cardroundtype);
  result.setFormroundtype(obj.formroundtype);
  result.setBookingformborder(obj.bookingformborder);
  result.setBookingadvantagesicon(obj.bookingadvantagesicon);
  result.setHeadercolor(colorsFromObject(obj.headercolor));
  result.setBodycolor(colorsFromObject(obj.bodycolor));
  result.setButtoncolor(colorsFromObject(obj.buttoncolor));
  result.setTagcolor(colorsFromObject(obj.tagcolor));
  result.setBookingadvantagescolor(colorsFromObject(obj.bookingadvantagescolor));
  result.setLogo(multimediaFromObject(obj.logo));
  result.setEmaillogo(multimediaFromObject(obj.emaillogo));
  result.setFavicon(multimediaFromObject(obj.favicon));
  result.setMobilenavigationstyle(obj.mobilenavigationstyle);
  result.setDesktopnavigationstyle(obj.desktopnavigationstyle);
  result.setDisplaymenutype(obj.displaymenutype);
  result.setBookingadvantagevariant(bookingAdvantageVariantToProto(obj.bookingadvantagevariant));
  result.setBookingadvantageposition(bookingAdvantagePositionToProto(obj.bookingadvantageposition));
  result.setBookingloyaltybarposition(
    bookingAdvantagePositionToProto(obj.bookingloyaltybarposition)
  );
  return result;
}

export async function getEngineAppearance(): Promise<EngineAppearanceEntity> {
  const request = new GetPropertyConfigurationRequest();
  const getPropertyConfiguration = addAuthzMetadataGrpcCall(
    propertyconfigurationClient,
    propertyconfigurationClient.getPropertyConfiguration
  );
  const response = await getPropertyConfiguration(request);
  const engineAppearance = response.getConfiguration()?.toObject().engineappearance;
  if (!engineAppearance) {
    throw new Error("Unexpected reply obtaining engineAppearance");
  }
  return { ...engineAppearance, id: "engineAppearance" };
}

function imageFromMultimedia(multimedia: Multimedia.AsObject): Image[] {
  if (multimedia.filename === "empty.png") {
    return [];
  }
  return [
    Object.assign(new File([""], multimedia.filename), {
      id: multimedia.id,
      url: multimedia.url,
      filename: multimedia.filename,
    }),
  ];
}

export async function getSimpleEngineAppearance(): Promise<SimpleEngineAppearanceEntity> {
  const engineAppearanceEntity = await getEngineAppearance();
  return {
    id: engineAppearanceEntity.id,
    primaryColor: engineAppearanceEntity.palette?.primary?.main ?? "rgba(0, 143, 105, 1)",
    secondaryColor: engineAppearanceEntity.palette?.secondary?.main ?? "rgba(0, 143, 105, 1)",
    logo: engineAppearanceEntity.logo ? imageFromMultimedia(engineAppearanceEntity.logo) : [],
    emaillogo: engineAppearanceEntity.emaillogo
      ? imageFromMultimedia(engineAppearanceEntity.emaillogo)
      : [],
    favicon: engineAppearanceEntity.favicon
      ? imageFromMultimedia(engineAppearanceEntity.favicon)
      : [],
  };
}

export async function updateEngineAppearance(entity: EngineAppearanceEntity): Promise<void> {
  const request = new UpdateEngineAppearanceRequest();
  const engineAppearanceEntity = engineAppearanceFromObject(entity);
  request.setEngineappearance(engineAppearanceEntity);
  const updateEngineAppearance = addAuthzMetadataGrpcCall(
    propertyconfigurationClient,
    propertyconfigurationClient.updateEngineAppearance
  );
  await updateEngineAppearance(request);
}

// we do not need the palette inside the engineAppearance to have a distinct UUID
const baseUUID = "3639076e-8f86-442f-b1c6-d123b718b9c7";
export async function updateSimpleEngineAppearance(
  entity: SimpleEngineAppearanceEntity
): Promise<void> {
  const engineAppearanceEntity = await getEngineAppearance();
  const newEntity: EngineAppearanceEntity = {
    ...engineAppearanceEntity,
    palette: {
      ...engineAppearanceEntity.palette,
      id: baseUUID,
      ticker: "mainPalette",
      type: PaletteType.PUBLIC,
      primary: {
        main: entity.primaryColor,
        light: engineAppearanceEntity.palette?.primary?.light ?? entity.primaryColor,
        dark: engineAppearanceEntity.palette?.primary?.dark ?? entity.primaryColor,
        contrasttext: oppositeColorHex(entity.primaryColor),
      },
      secondary: {
        main: entity.secondaryColor,
        light: engineAppearanceEntity.palette?.secondary?.light ?? entity.secondaryColor,
        dark: engineAppearanceEntity.palette?.secondary?.dark ?? entity.secondaryColor,
        contrasttext: oppositeColorHex(entity.secondaryColor),
      },
    },
    logo: engineAppearanceEntity.logo,
    emaillogo: engineAppearanceEntity.emaillogo,
    favicon: engineAppearanceEntity.favicon,
  };
  await updateEngineAppearance(newEntity);
}
